import * as React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";

interface Chat {
  id: string;
  createdAt: string;
  title: string;
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const day = date.getDate();
  const month = date.toLocaleString("default", { month: "short" });
  const year = date.getFullYear();
  const time = date.toLocaleString("en-US", {
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  });

  const getDaySuffix = (day: number) => {
    if (day > 3 && day < 21) return "th";
    switch (day % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };

  const formattedDate = `${day}${getDaySuffix(day)} ${month}`;

  if (year === now.getFullYear()) {
    return `${formattedDate}, ${time}`;
  }
  return `${formattedDate} ${year}, ${time}`;
};

export function ChatHistory() {
  const [chats, setChats] = React.useState<Chat[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const navigate = useNavigate();
  const { chatId } = useParams<{ chatId: string }>();

  React.useEffect(() => {
    const fetchChats = async () => {
      try {
        const response = await fetch(
          `${import.meta.env.VITE_PUBLIC_API_ENDPOINT}/api/chats`,
          {
            credentials: "include",
          },
        );
        if (response.ok) {
          const data = await response.json();
          setChats(data);
        } else {
          setError("Failed to fetch chat history");
        }
      } catch {
        setError("An error occurred while fetching chat history");
      } finally {
        setLoading(false);
      }
    };

    fetchChats();
  }, []);

  const handleNewChat = () => {
    navigate("/chat");
  };

  const handleChatClick = (id: string) => {
    navigate(`/chat/${id}`);
  };

  return (
    <aside className="hidden h-full w-80 flex-col border-l bg-background lg:flex">
      <div className="p-4 pb-2">
        <Button
          onClick={handleNewChat}
          variant={!chatId ? "secondary" : "ghost"}
          className={cn(
            "w-full justify-start gap-2",
            !chatId && "bg-secondary font-medium",
          )}
        >
          <Sparkles className="h-4 w-4" />
          New chat
        </Button>
      </div>
      <ScrollArea className="flex-1 px-4">
        <div className="space-y-2 py-2">
          {loading ? (
            <div className="text-center text-sm text-muted-foreground">
              Loading...
            </div>
          ) : error ? (
            <div className="text-center text-sm text-destructive">{error}</div>
          ) : (
            <div className="flex flex-col gap-1">
              {chats.map((chat) => (
                <Button
                  key={chat.id}
                  variant={chatId === chat.id ? "secondary" : "ghost"}
                  className={cn(
                    "h-auto w-full justify-start gap-2 px-3 py-3",
                    chatId === chat.id && "bg-secondary font-medium",
                  )}
                  onClick={() => handleChatClick(chat.id)}
                >

                  <div className="flex w-full flex-col items-start gap-1 overflow-hidden">
                    <span className="w-full truncate text-left text-sm">
                      {chat.title || "Untitled Chat"}
                    </span>
                    <span className="text-xs font-normal text-muted-foreground">
                      {formatDate(chat.createdAt)}
                    </span>
                  </div>
                </Button>
              ))}
            </div>
          )}
        </div>
      </ScrollArea>
    </aside>
  );
}
